import React, { useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ToggleFilter } from '@pcln/horizon'
import { setEmployeeDiscountEnabled } from '../../../reducers/filters/employeeDiscount/employeeDiscount'
import { updateAirlineFiltersUI } from '../../../reducers/filters/airlinesFilter/airlinesFilter'
import { useDebouncedListingsFilterCall } from '../useDebouncedListingsFilterCall/useDebouncedListingsFilterCall'
import PCLNAnalytics from '@pcln/analytics'

export const EmployeeDiscount = () => {
  const dispatch = useDispatch()
  const { debouncedListingsFilterCall } = useDebouncedListingsFilterCall()
  const { isEnabled, standardSessionKey, employeeSessionKey } = useSelector((state) => ({
    isEnabled: state.employeeDiscount?.isEnabled || false,
    standardSessionKey: state.employeeDiscount?.standardSessionKey || '',
    employeeSessionKey: state.employeeDiscount?.employeeSessionKey || '',
  }))
  const pclnBootstrapData = useSelector((state) => state.pclnBootstrapData)

  // Use ref to track if we're currently processing a toggle to prevent duplicates
  const isProcessingToggle = useRef(false)

  const handleToggle = useCallback(
    (enabled) => {
      // Prevent duplicate calls if we're already processing a toggle
      if (isProcessingToggle.current) {
        return
      }

      // Set processing flag
      isProcessingToggle.current = true

      // Production logging for toggle click
      PCLNAnalytics.log({
        category: 'Flight Listings',
        action: 'Employee Discount Toggle Clicked',
        label: enabled ? 'EMPLOYEE_DISCOUNT_ENABLED' : 'EMPLOYEE_DISCOUNT_DISABLED',
        employeeDiscountEnabled: enabled,
        hasEmployeeSessionKey: !!employeeSessionKey,
        hasStandardSessionKey: !!standardSessionKey,
      })

      // Update the toggle state in Redux
      dispatch(setEmployeeDiscountEnabled(enabled))

      // Reset airline filter UI state to prevent sync issues
      // This ensures the UI reflects the actual filter state from the backend
      dispatch(updateAirlineFiltersUI({ payload: {}, meta: { isNoneClick: true } }))

      // Use debounced listingsFilterCall to avoid duplicate API calls
      debouncedListingsFilterCall(pclnBootstrapData, 'employeeDiscountToggle')

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingToggle.current = false
      }, 100)
    },
    [dispatch, debouncedListingsFilterCall, pclnBootstrapData, standardSessionKey, employeeSessionKey]
  )

  return (
    <div className='mb-4'>
      <ToggleFilter
        key={`employee-discount-${isEnabled}`}
        label='Employee Discount'
        helperText='Thanks for being part of the Priceline team! Turn on to reveal employee discounts.'
        defaultEnabled={isEnabled}
        onToggleChange={handleToggle}
        toggleProps={{ checked: isEnabled }}
      />
    </div>
  )
}
